import { Controller } from "@hotwired/stimulus"

// Floating AI-assistant widget (#chat v1). Opens/closes the panel, lazily loads the
// right conversation into the Turbo Frame (deal-aware when the page carries
// data-chat-lead-id), shows a typing bubble, and echoes the user's own message
// instantly so it doesn't wait on the cable round-trip. The authoritative turns still
// arrive over the per-conversation Turbo Stream; the echo is a temporary placeholder
// that the real broadcast replaces (see onStreamRender → clearOptimistic).
export default class extends Controller {
  static targets = ["panel", "launcher", "frame", "messages", "typingSlot", "input", "form"]

  connect() {
    // A turn is being rendered into the thread — from the HTTP response (the sender's
    // own request) or a cable broadcast (other participants / redundant copy). Each
    // message carries a stable id, and both paths can deliver it, so:
    //   • skip a render whose message id is already present (dedupe + no reorder), and
    //   • otherwise drop the optimistic echo, since the real bubble is landing.
    this.onStreamRender = (event) => {
      const box = this.messagesBox
      const stream = event.target
      const target = stream && stream.getAttribute && stream.getAttribute("target")
      if (!box || target !== box.id) return

      const tpl = stream.querySelector && stream.querySelector("template")
      const incoming = tpl && tpl.content && tpl.content.firstElementChild
      if (incoming && incoming.id && document.getElementById(incoming.id)) {
        event.preventDefault() // already have this message — don't append a duplicate
        return
      }

      this.clearOptimistic()
      requestAnimationFrame(() => this.scrollToBottom())
    }
    document.addEventListener("turbo:before-stream-render", this.onStreamRender)
  }

  disconnect() {
    document.removeEventListener("turbo:before-stream-render", this.onStreamRender)
  }

  toggle() {
    this.isOpen ? this.close() : this.open()
  }

  open() {
    this.panelTarget.classList.remove("hidden")
    const url = this.conversationUrl()
    // Reload the frame only when the target thread changed (general ↔ a deal), so a
    // re-open doesn't refetch and comparing our own value dodges relative/absolute skew.
    if (this.loadedUrl !== url) {
      this.loadedUrl = url
      this.frameTarget.setAttribute("src", url)
    }
    setTimeout(() => { this.focusInput(); this.scrollToBottom() }, 150)
  }

  close() {
    this.panelTarget.classList.add("hidden")
  }

  get isOpen() {
    return !this.panelTarget.classList.contains("hidden")
  }

  // A deal thread when the current page is a lead, else the general support thread.
  // Re-read on every open — Turbo navigation swaps pages without reconnecting us.
  conversationUrl() {
    const el = document.querySelector("[data-chat-lead-id]")
    const leadId = el && el.getAttribute("data-chat-lead-id")
    return leadId ? `/leads/${leadId}/conversation` : "/conversation"
  }

  submitOnEnter(event) {
    if (event.key !== "Enter" || event.shiftKey) return
    event.preventDefault()
    const form = event.target.closest("form")
    if (form && event.target.value.trim()) form.requestSubmit()
  }

  // Submit start. Turbo has already captured the form body, so we now (a) echo the
  // message into the log immediately — so it appears instantly instead of waiting for
  // the cable round-trip — (b) clear the composer, stashing the text for a failed send,
  // and (c) show a typing bubble. The real, persisted bubble replaces the echo when its
  // broadcast lands (see onStreamRender → clearOptimistic).
  showTyping() {
    const body = this.hasInputTarget ? this.inputTarget.value : ""
    if (!body.trim()) return // nothing to send; the server no-ops a blank body

    this.pendingBody = body
    if (this.hasInputTarget) this.inputTarget.value = ""
    this.appendOptimistic(body)

    const slot = this.typingSlot
    if (slot) {
      slot.innerHTML =
        '<div class="flex justify-start p-1"><div class="rounded-2xl rounded-tl-sm bg-base-200 px-3 py-2">' +
        '<span class="loading loading-dots loading-sm"></span></div></div>'
    }
    this.scrollToBottom()
  }

  // Submit end. Turbo fires this for failures too, so on a failed send drop the echo
  // and restore the user's text (cleared on submit-start) instead of losing it.
  afterSubmit(event) {
    const slot = this.typingSlot
    if (slot) slot.innerHTML = ""
    if (event && event.detail && event.detail.success === false) {
      this.clearOptimistic()
      if (this.hasInputTarget && this.pendingBody) this.inputTarget.value = this.pendingBody
      this.showFlash("Couldn't send your message — check your connection and try again.")
    }
    this.pendingBody = null
    this.focusInput()
    this.scrollToBottom()
  }

  // A stand-in for the user's own message, shown instantly and swapped for the real
  // persisted bubble when the broadcast arrives. Slightly faded to read as "sending".
  appendOptimistic(text) {
    const box = this.messagesBox
    if (!box) return
    const row = document.createElement("div")
    row.className = "flex justify-end"
    row.setAttribute("data-optimistic", "true")
    const bubble = document.createElement("div")
    bubble.className = "max-w-[85%] rounded-2xl rounded-tr-sm bg-primary px-3 py-2 text-sm text-primary-content opacity-60"
    const inner = document.createElement("div")
    inner.className = "whitespace-pre-wrap"
    inner.textContent = text // textContent, never innerHTML — no XSS from user input
    bubble.appendChild(inner)
    row.appendChild(bubble)
    box.appendChild(row)
  }

  clearOptimistic() {
    const box = this.messagesBox
    if (box) box.querySelectorAll("[data-optimistic]").forEach((el) => el.remove())
  }

  // Drop a transient note into the flash slot; it self-dismisses via chat-flash.
  showFlash(text) {
    const el = this.element.querySelector("#chat_flash")
    if (!el) return
    const note = document.createElement("div")
    note.setAttribute("data-controller", "chat-flash")
    note.textContent = text
    el.replaceChildren(note)
  }

  get typingSlot() {
    return this.hasTypingSlotTarget ? this.typingSlotTarget : this.element.querySelector("#chat_typing_slot")
  }

  get messagesBox() {
    return this.hasMessagesTarget ? this.messagesTarget : this.element.querySelector("[data-chat-target='messages']")
  }

  focusInput() {
    if (this.hasInputTarget) this.inputTarget.focus()
  }

  scrollToBottom() {
    const box = this.messagesBox
    if (box) box.scrollTop = box.scrollHeight
  }
}
